import Phaser from 'phaser';

/**
 * DeathReplaySystem - Plays a slow-motion looping replay of the killing blow
 */
export class DeathReplaySystem {
    private scene: Phaser.Scene;
    private replayData: { x: number; y: number; time: number }[] = [];
    private isReplaying: boolean = false;
    private replayTween?: Phaser.Tweens.Tween;
    private replayOverlay?: Phaser.GameObjects.Rectangle;
    private fartVisual?: Phaser.GameObjects.Arc;
    private texts: Phaser.GameObjects.Text[] = [];

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
    }

    /**
     * Records the position of the killing fart and the victim at impact
     */
    captureKillingBlow(
        victimX: number,
        victimY: number,
        fartX: number,
        fartY: number
    ): void {
        this.replayData = [
            { x: fartX, y: fartY, time: 0 },
            { x: victimX, y: victimY, time: 600 },
        ];
    }

    playLoopingReplay(onDismiss: () => void): void {
        if (this.replayData.length < 2) {
            onDismiss(); // Nothing to show
            return;
        }

        this.isReplaying = true;

        // Darken background slightly
        this.replayOverlay = this.scene.add.rectangle(
            0, 0,
            this.scene.cameras.main.width,
            this.scene.cameras.main.height,
            0x000000, 0.4
        ).setOrigin(0, 0).setScrollFactor(0).setDepth(499);

        // Create replay visual (fart circle)
        this.fartVisual = this.scene.add.circle(
            this.replayData[0].x,
            this.replayData[0].y,
            16, 0xfdcb6e
        ).setDepth(500);

        // Slow-mo text
        const slowmoText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            80,
            '💀 KILLED BY...',
            { fontSize: '32px', color: '#ff7675', fontFamily: 'Arial Black', stroke: '#000', strokeThickness: 4 }
        ).setOrigin(0.5).setScrollFactor(0).setDepth(501);

        // Tap to dismiss
        const dismissText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.height - 80,
            'Tap anywhere to spectate',
            { fontSize: '18px', color: '#ffffff', fontFamily: 'Arial', backgroundColor: '#000000a0', padding: { x: 10, y: 5 } }
        ).setOrigin(0.5).setScrollFactor(0).setDepth(501);

        this.texts = [slowmoText, dismissText];

        // Loop animation
        const playOnce = () => {
            if (!this.isReplaying || !this.fartVisual) return;

            this.fartVisual.setPosition(this.replayData[0].x, this.replayData[0].y);
            this.fartVisual.setScale(0.5);
            this.fartVisual.setAlpha(1);

            this.replayTween = this.scene.tweens.add({
                targets: this.fartVisual,
                x: this.replayData[1].x,
                y: this.replayData[1].y,
                scale: 2,
                alpha: 0.3,
                duration: 1500, // Slow mo (real travel is ~600ms)
                ease: 'Power2',
                onComplete: () => {
                    if (this.isReplaying) {
                        this.scene.time.delayedCall(800, playOnce);
                    }
                }
            });
        };

        playOnce();

        // Dismiss on tap
        this.scene.input.once('pointerdown', () => {
            this.stop();
            onDismiss();
        });
    }

    private stop(): void {
        this.isReplaying = false;
        this.replayTween?.stop();
        this.fartVisual?.destroy();
        this.replayOverlay?.destroy();
        this.texts.forEach(t => t.destroy());
    }
}
